import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import api from '../api/api';
import '../styles/EditCenterPage.css';

const EditCenterPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [center, setCenter] = useState({
    name: '',
    address: '',
    contactEmail: '',
    contactPhone: '',
    logoUrl: null,
    subdomainPrefix: '',
    brandingSettings: {
      primaryColor: '#4a90e2',
      secondaryColor: '#333333',
      accentColor: '#ff9800'
    }
  });
  const [originalSubdomain, setOriginalSubdomain] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Get centerId from URL params if available
  const { centerId } = useParams();
  
  useEffect(() => {
    // Check if user has permission to edit center
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/dashboard');
      return;
    }

    // Fetch center data - either from params or user's center
    if (centerId) {
      // Super admin editing a specific center
      if (user.role === 'super_admin') {
        fetchCenterData(centerId);
      } else {
        // Non-super admin cannot edit other centers
        navigate('/dashboard');
      }
    } else if (user.center) {
      // Normal flow - admin editing their own center
      fetchCenterData(user.center);
    } else {
      setLoading(false);
    }
  }, [user, navigate, centerId]);

  const fetchCenterData = async (centerId) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get(`/centers/${centerId}`);
      
      if (response.data) {
        const centerData = response.data.success ? response.data.data : response.data;
        
        setCenter({
          name: centerData.name || '',
          address: centerData.address || '',
          contactEmail: centerData.contactEmail || centerData.email || '',
          contactPhone: centerData.contactPhone || centerData.phone || '',
          logoUrl: centerData.logoUrl || null,
          subdomainPrefix: centerData.subdomainPrefix || '',
          brandingSettings: {
            primaryColor: centerData.brandingSettings?.primaryColor || '#4a90e2',
            secondaryColor: centerData.brandingSettings?.secondaryColor || '#333333',
            accentColor: centerData.brandingSettings?.accentColor || '#ff9800'
          }
        });
        
        // Store original subdomain for comparison during save
        setOriginalSubdomain(centerData.subdomainPrefix || '');
        
        // If center has a logo, set the preview
        if (centerData.logoUrl) {
          setLogoPreview(centerData.logoUrl);
        }
      }
    } catch (err) {
      console.error('Error fetching center data:', err);
      setError('Failed to load center data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      // Handle nested properties (for branding settings)
      const [parent, child] = name.split('.');
      setCenter(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setCenter(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!center.name || !center.contactEmail) {
      setError('Please fill in all required fields.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      // Log submission details for debugging
      console.log('Submitting center data:', {
        ...center,
        subdomainPrefix: center.subdomainPrefix,
        originalSubdomain,
        isSubdomainChanged: center.subdomainPrefix !== originalSubdomain,
        userRole: user.role
      });
      
      // Determine which center ID to use (from URL params or user context)
      const targetCenterId = centerId || user.center;
      if (!targetCenterId) {
        throw new Error('No center ID available for update');
      }
      
      // For super admin, always use the branding endpoint to ensure subdomain changes are processed
      let endpoint = user.role === 'super_admin' 
        ? `/centers/${targetCenterId}/branding`
        : `/centers/${targetCenterId}`;
      
      console.log('Using endpoint:', endpoint);
      
      // For super admin, use JSON data instead of FormData for better error handling and debugging
      if (user.role === 'super_admin') {
        // Prepare data as JSON
        const updateData = {
          name: center.name,
          address: center.address || '',
          contactEmail: center.contactEmail,
          contactPhone: center.contactPhone || '',
          subdomainPrefix: center.subdomainPrefix || '',
          brandingSettings: {
            primaryColor: center.brandingSettings?.primaryColor || '#4a90e2',
            secondaryColor: center.brandingSettings?.secondaryColor || '#333333',
            accentColor: center.brandingSettings?.accentColor || '#ff9800'
          }
        };
        
        console.log('Sending data:', updateData);
        
        // Send update request with JSON
        const response = await api.put(endpoint, updateData);
        
        if (response.data) {
          console.log('Update successful:', response.data);
          setSuccess(true);
          
          // Navigate back to appropriate page after success
          setTimeout(() => {
            if (centerId) {
              navigate(`/center/details/${targetCenterId}`);
            } else {
              navigate('/dashboard');
            }
          }, 1500);
        }
      } else {
        // For non-super admin, continue using FormData for file uploads
        const formData = new FormData();
        formData.append('name', center.name);
        formData.append('address', center.address || '');
        formData.append('contactEmail', center.contactEmail);
        formData.append('contactPhone', center.contactPhone || '');
        
        // If there's a new logo file, append it
        if (logoFile) {
          formData.append('logo', logoFile);
        }
        
        const response = await api.put(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data) {
          console.log('Update successful:', response.data);
          setSuccess(true);
          
          // Navigate back to dashboard after success
          setTimeout(() => {
            navigate('/dashboard');
          }, 1500);
        }
      }
    } catch (err) {
      console.error('Error updating center:', err);
      setError(`Failed to update center information: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="edit-center-container">
      <div className="header">
        <div className="header-left">
          <Link to="/settings" className="back-link">
            &larr; Back to Settings
          </Link>
          <h1>Edit Center Information</h1>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading center data...</p>
        </div>
      ) : (
        <div className="edit-center-content">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">Center information updated successfully!</div>}
          
          <form onSubmit={handleSubmit} className="center-form">
            <div className="form-sections">
              <div className="form-section logo-section">
                <h2>Center Logo</h2>
                <div className="logo-container">
                  <div className="logo-preview">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Center Logo" className="logo-image" />
                    ) : (
                      <div className="logo-placeholder">
                        <i className="placeholder-icon">📷</i>
                        <span>No Logo</span>
                      </div>
                    )}
                  </div>
                  <label className="logo-upload-button">
                    {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLogoChange} 
                      className="file-input"
                    />
                  </label>
                </div>
              </div>

              <div className="form-section center-details">
                <h2>Center Details</h2>
                
                <div className="form-group">
                  <label htmlFor="name">Center Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={center.name}
                    onChange={handleInputChange}
                    placeholder="Enter center name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    name="address"
                    value={center.address}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                    rows="3"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="contactEmail">Contact Email *</label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={center.contactEmail}
                    onChange={handleInputChange}
                    placeholder="Enter contact email"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="contactPhone">Contact Phone</label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={center.contactPhone}
                    onChange={handleInputChange}
                    placeholder="Enter contact phone"
                  />
                </div>
              </div>
              
              {/* Branding section - only shown for super_admin */}
              {user && user.role === 'super_admin' && (
                <div className="form-section branding-section">
                  <h2>Branding Settings</h2>
                  
                  <div className="form-group">
                    <label htmlFor="subdomainPrefix">Subdomain Prefix</label>
                    <div className="subdomain-input-group">
                      <input
                        type="text"
                        id="subdomainPrefix"
                        name="subdomainPrefix"
                        value={center.subdomainPrefix}
                        onChange={handleInputChange}
                        placeholder="Enter subdomain prefix"
                        pattern="[a-z0-9-]+"
                      />
                      <span className="subdomain-suffix">.memoraize.io</span>
                    </div>
                    <small className="help-text">Only lowercase letters, numbers, and hyphens allowed</small>
                  </div>
                  
                  <div className="color-pickers">
                    <div className="form-group">
                      <label htmlFor="brandingSettings.primaryColor">Primary Color</label>
                      <div className="color-input-group">
                        <input
                          type="color"
                          id="brandingSettings.primaryColor"
                          name="brandingSettings.primaryColor"
                          value={center.brandingSettings.primaryColor}
                          onChange={handleInputChange}
                        />
                        <input
                          type="text"
                          value={center.brandingSettings.primaryColor}
                          name="brandingSettings.primaryColor"
                          onChange={handleInputChange}
                          className="color-text-input"
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="brandingSettings.secondaryColor">Secondary Color</label>
                      <div className="color-input-group">
                        <input
                          type="color"
                          id="brandingSettings.secondaryColor"
                          name="brandingSettings.secondaryColor"
                          value={center.brandingSettings.secondaryColor}
                          onChange={handleInputChange}
                        />
                        <input
                          type="text"
                          value={center.brandingSettings.secondaryColor}
                          name="brandingSettings.secondaryColor"
                          onChange={handleInputChange}
                          className="color-text-input"
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="brandingSettings.accentColor">Accent Color</label>
                      <div className="color-input-group">
                        <input
                          type="color"
                          id="brandingSettings.accentColor"
                          name="brandingSettings.accentColor"
                          value={center.brandingSettings.accentColor}
                          onChange={handleInputChange}
                        />
                        <input
                          type="text"
                          value={center.brandingSettings.accentColor}
                          name="brandingSettings.accentColor"
                          onChange={handleInputChange}
                          className="color-text-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="branding-preview">
                    <h3>Preview</h3>
                    <div className="color-preview" style={{ backgroundColor: center.brandingSettings.primaryColor }}>Primary</div>
                    <div className="color-preview" style={{ backgroundColor: center.brandingSettings.secondaryColor, color: '#fff' }}>Secondary</div>
                    <div className="color-preview" style={{ backgroundColor: center.brandingSettings.accentColor }}>Accent</div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => navigate('/settings')}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="save-button"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EditCenterPage;